import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PosAgentAdminService } from '../../../services/pos-agent-admin.service';
import { RemoteControlAdminService } from '../../../services/remote-control-admin.service';

@Component({
  selector: 'app-desktop-overlay',
  templateUrl: './desktop-overlay.component.html',
  styleUrls: ['./desktop-overlay.component.css'],
})
export class DesktopOverlayComponent implements OnInit, OnDestroy {
  @Input() terminalId!: string;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('desktopImg') desktopImgRef!: ElementRef<HTMLImageElement>;

  // Position & size
  top = 60;
  left = 60;
  width = 900;
  height = 560;

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartTop = 0;
  private dragStartLeft = 0;

  // Resize state
  private isResizing = false;
  private resizeDir = '';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartW = 0;
  private resizeStartH = 0;
  private resizeStartTop = 0;
  private resizeStartLeft = 0;

  currentFrameSrc: string | null = null;
  controlId: string | null = null;

  // FPS counter
  fps = 0;
  private frameCount = 0;
  private fpsInterval: any = null;

  private subs = new Subscription();

  constructor(
    public posAgentAdminService: PosAgentAdminService,
    private remoteControlService: RemoteControlAdminService,
  ) {}

  ngOnInit(): void {
    const socket = this.remoteControlService.getSocket();
    if (!socket) {
      console.error('[DesktopOverlay] No socket available — cannot start desktop control');
      this.closed.emit();
      return;
    }

    this.controlId = this.posAgentAdminService.requestDesktopControl(socket, this.terminalId);

    this.subs.add(
      this.posAgentAdminService.desktopFrame$.subscribe(frame => {
        if (frame.controlId === this.controlId) {
          this.currentFrameSrc = 'data:image/jpeg;base64,' + frame.jpeg;
          this.frameCount++;
        }
      }),
    );

    this.subs.add(
      this.posAgentAdminService.controlEnded$.subscribe(cid => {
        if (cid === this.controlId) {
          this.closed.emit();
        }
      }),
    );

    this.fpsInterval = setInterval(() => {
      this.fps = this.frameCount;
      this.frameCount = 0;
    }, 1000);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.fpsInterval !== null) {
      clearInterval(this.fpsInterval);
    }
    if (this.controlId) {
      const socket = this.remoteControlService.getSocket();
      if (socket) {
        this.posAgentAdminService.endDesktopControl(socket, this.controlId);
      }
    }
  }

  endControl(): void {
    if (this.controlId) {
      const socket = this.remoteControlService.getSocket();
      if (socket) {
        this.posAgentAdminService.endDesktopControl(socket, this.controlId);
      }
      this.controlId = null;
    }
    this.closed.emit();
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  startDrag(e: MouseEvent): void {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartTop = this.top;
    this.dragStartLeft = this.left;
    e.preventDefault();
  }

  // ── Resize ────────────────────────────────────────────────────────────────

  startResize(e: MouseEvent, dir: string): void {
    this.isResizing = true;
    this.resizeDir = dir;
    this.resizeStartX = e.clientX;
    this.resizeStartY = e.clientY;
    this.resizeStartW = this.width;
    this.resizeStartH = this.height;
    this.resizeStartTop = this.top;
    this.resizeStartLeft = this.left;
    e.preventDefault();
    e.stopPropagation();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(e: MouseEvent): void {
    if (this.isDragging) {
      this.top = this.dragStartTop + (e.clientY - this.dragStartY);
      this.left = this.dragStartLeft + (e.clientX - this.dragStartX);
    } else if (this.isResizing) {
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      const minW = 400;
      const minH = 250;

      if (this.resizeDir.includes('e')) {
        this.width = Math.max(minW, this.resizeStartW + dx);
      }
      if (this.resizeDir.includes('s')) {
        this.height = Math.max(minH, this.resizeStartH + dy);
      }
      if (this.resizeDir.includes('w')) {
        const newW = Math.max(minW, this.resizeStartW - dx);
        this.left = this.resizeStartLeft + (this.resizeStartW - newW);
        this.width = newW;
      }
      if (this.resizeDir.includes('n')) {
        const newH = Math.max(minH, this.resizeStartH - dy);
        this.top = this.resizeStartTop + (this.resizeStartH - newH);
        this.height = newH;
      }
    }
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  onOverlayMouseDown(e: MouseEvent): void {
    e.stopPropagation();
  }

  // ── Input forwarding ──────────────────────────────────────────────────────

  private get imgEl(): HTMLImageElement | null {
    return this.desktopImgRef?.nativeElement ?? null;
  }

  private get agentInfo() {
    return this.posAgentAdminService.getAgentInfo(this.terminalId);
  }

  private sendInput(event: MouseEvent | WheelEvent | KeyboardEvent): void {
    if (!this.controlId || !this.imgEl) return;
    const socket = this.remoteControlService.getSocket();
    if (!socket) return;
    const info = this.agentInfo;
    this.posAgentAdminService.sendDesktopInput(
      socket,
      this.controlId,
      event,
      this.imgEl,
      info?.screenWidth ?? 1920,
      info?.screenHeight ?? 1080,
    );
  }

  onImgMouseDown(e: MouseEvent): void {
    this.imgEl?.focus();
    this.sendInput(e);
  }

  onImgMouseUp(e: MouseEvent): void {
    this.sendInput(e);
  }

  onImgMouseMove(e: MouseEvent): void {
    this.sendInput(e);
  }

  onImgDblClick(e: MouseEvent): void {
    this.sendInput(e);
  }

  onImgWheel(e: WheelEvent): void {
    e.preventDefault();
    this.sendInput(e);
  }

  onImgKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    this.sendInput(e);
  }

  onImgKeyUp(e: KeyboardEvent): void {
    e.preventDefault();
    this.sendInput(e);
  }
}
